import React from 'react';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import RevokeSessionLink from './revokeSession/RevokeSessionLink';
import styled from 'styled-components';
import { COLORS } from '../../app/common/constants/styles';
import { Link } from 'react-router-dom';
import { UnionCardIcon } from '../../app/common/components/icons/UnionCardIcon';
import { RegionHelper } from '../../utils/RegionHelper';
import { addRatEvent } from '../../utils/ratClickEvents';

const IconWrapper = styled.span`
    display: inline-block;
    margin-left: 3px;
`;

const Email2FALabelWrapper = styled.span`
    display: flex;
    gap: 8px;
`;
const Email2FAEnabled = styled.span`
    color: #049907;
`;
const Email2FADisabled = styled.span`
    color: #bf0000;
`;

const LinkWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
`;
export const CancelMemberLink = styled(Link)`
    color: ${COLORS.rakutenRed};
    cursor: pointer;
    transition: font-weight 200ms ease-in;
    text-decoration: underline;
    &:hover {
        font-weight: bold;
    }
    margin-top: 20px;
    margin-bottom: 20px;
`;
const SecurityItemLabel = styled.div`
    display: flex;
    flex-direction: column;
    color: #4d4d4d;
    font-size: 16px;
    font-weight: 300;
    line-height: normal;

    /* Media Query */
    @media screen and (min-width: 900px) {
        flex-direction: row;
        gap: 8px;
    }
`;
const ExternalLinkWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
`;

const SecurityItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    border-bottom: solid 1px #d1d1d1;
    padding: 25px 10px;
`;

const SecurityItems = styled.div`
    margin-bottom: 40px;
`;

export const ExternalLink = styled.a`
    color: ${COLORS.linkBlue};
    cursor: pointer;
    transition: font-weight 200ms ease-in;
    text-decoration: underline;
    &:hover {
        font-weight: bold;
    }
    & + & {
        margin-top: 30px;
    }
`;
const Head = styled.h1`
    font-size: 1.75rem;
    font-weight: 400;
    margin: 18px auto 26px 0;
`;
interface SecuritySectionProps {
    cancelMember: boolean;
    cancelMembershipLink: string;
    cancelMembershipLinkText: string;
    sessionRevocationEnabled: boolean;
    isSessionRevoked: boolean;
    enabled2FA?: boolean;
    errorRevokingSession: string;
    showSecretQuestion?: boolean;
    secretQuestionAnswerUrl: string;
    email2faFAQUrl: string;
    enableUserEmail2FAinOmni: string;
    oamUrl: string;
    isTwIchiba: boolean;
    handleSessionRevocation: () => void;
}

function SecuritySection({
    cancelMember,
    cancelMembershipLink,
    cancelMembershipLinkText,
    sessionRevocationEnabled,
    isSessionRevoked,
    enabled2FA,
    errorRevokingSession,
    showSecretQuestion,
    secretQuestionAnswerUrl,
    email2faFAQUrl,
    enableUserEmail2FAinOmni,
    oamUrl,
    handleSessionRevocation,
    isTwIchiba,
}: SecuritySectionProps) {
    const showPassKeyLink = RegionHelper.isJP() || isTwIchiba;
    return (
        <>
            <div>
                <Head>
                    {TranslationHelpers.getFormattedMessage('account.security.security_header')}
                </Head>
                <SecurityItems>
                    <SecurityItem>
                        <SecurityItemLabel>
                            {TranslationHelpers.getFormattedMessage(
                                'account.security.external.link.email_label',
                            )}
                            {!RegionHelper.isEU() && (
                                <div>
                                    <ExternalLink
                                        onClick={() =>
                                            addRatEvent('security_section', 'details_email_2fa')
                                        }
                                        tabIndex={0}
                                        target="_blank"
                                        href={email2faFAQUrl}
                                    >
                                        {TranslationHelpers.getFormattedMessage(
                                            'account.security.external.link.email_details',
                                        )}
                                    </ExternalLink>
                                    <IconWrapper>
                                        <UnionCardIcon />
                                    </IconWrapper>
                                </div>
                            )}
                        </SecurityItemLabel>
                        <ExternalLinkWrapper>
                            <Email2FALabelWrapper>
                                {enabled2FA ? (
                                    <Email2FAEnabled>
                                        {TranslationHelpers.getFormattedMessage(
                                            'account.security.label.enabled',
                                        )}
                                    </Email2FAEnabled>
                                ) : (
                                    <>
                                        <Email2FADisabled>
                                            {TranslationHelpers.getFormattedMessage(
                                                'account.security.label.disabled',
                                            )}
                                        </Email2FADisabled>

                                        <ExternalLink
                                            onClick={() =>
                                                addRatEvent('security_section', 'enable_email_2fa')
                                            }
                                            tabIndex={0}
                                            href={
                                                enableUserEmail2FAinOmni +
                                                '&r10_own_scope=myr_email_2fa'
                                            }
                                        >
                                            {TranslationHelpers.getFormattedMessage(
                                                'account.security.email2FA.action.enroll',
                                            )}
                                        </ExternalLink>
                                    </>
                                )}
                            </Email2FALabelWrapper>
                        </ExternalLinkWrapper>
                    </SecurityItem>
                    {showPassKeyLink && (
                        <SecurityItem>
                            <SecurityItemLabel>
                                {TranslationHelpers.getFormattedMessage(
                                    'account.security.passkey.label',
                                )}
                            </SecurityItemLabel>
                            <ExternalLinkWrapper>
                                <ExternalLink
                                    onClick={() =>
                                        addRatEvent('security_section', 'passkey_manage')
                                    }
                                    tabIndex={0}
                                    target="_blank"
                                    href={oamUrl}
                                >
                                    {TranslationHelpers.getFormattedMessage(
                                        'account.security.external.link.passkey',
                                    )}
                                    <IconWrapper>
                                        <UnionCardIcon />
                                    </IconWrapper>
                                </ExternalLink>
                            </ExternalLinkWrapper>
                        </SecurityItem>
                    )}
                    {RegionHelper.isJP() && (
                        <SecurityItem>
                            <SecurityItemLabel>
                                {TranslationHelpers.getFormattedMessage(
                                    'account.security.external.link.secret_question_answer_label',
                                )}
                            </SecurityItemLabel>
                            <ExternalLinkWrapper>
                                {showSecretQuestion && (
                                    <ExternalLink
                                        onClick={() =>
                                            addRatEvent(
                                                'security_section',
                                                'setup_security_question',
                                            )
                                        }
                                        tabIndex={0}
                                        href={secretQuestionAnswerUrl}
                                    >
                                        {TranslationHelpers.getFormattedMessage(
                                            'account.security.external.link.secret_question_answer',
                                        )}
                                    </ExternalLink>
                                )}
                            </ExternalLinkWrapper>
                        </SecurityItem>
                    )}
                </SecurityItems>
                {sessionRevocationEnabled && (
                    <LinkWrapper>
                        <RevokeSessionLink
                            onClick={handleSessionRevocation}
                            isSessionRevoked={isSessionRevoked}
                            tooltipText={TranslationHelpers.getMessageDescriptor(
                                'tooltip.revoke_session',
                            )}
                            apiError={errorRevokingSession}
                        />
                    </LinkWrapper>
                )}
                {cancelMember && (
                    <LinkWrapper>
                        <CancelMemberLink
                            onClick={() => addRatEvent('member_cancellation', 'cancel_member')}
                            data-qa-id="cancelMember"
                            to={cancelMembershipLink}
                        >
                            {TranslationHelpers.getFormattedMessage(cancelMembershipLinkText)}
                        </CancelMemberLink>
                    </LinkWrapper>
                )}
            </div>
        </>
    );
}

export default SecuritySection;
